import { useState, useEffect, useMemo } from "react";
// import { aGet } from "../../utils/adminApi";
import { sGet } from "../../utils/staffApi";
import {
  C,
  Stat,
  BarChart,
  LoadingSpinner,
  Badge,
  Table,
  STATUS_COLORS,
  Btn,
} from "./AdminPanel";
import "../../styles/dashboard.css";

import {
  FiRefreshCw,
  FiDownload,
  FiBox,
  FiTrendingUp,
  FiTag,
  FiUsers,
  FiClock,
  FiArrowRight,
  FiAlertTriangle,
} from "react-icons/fi";
import { FaRupeeSign, FaTrophy } from "react-icons/fa";
import { MdMonetizationOn, MdShoppingCart } from "react-icons/md";

export default function Dashboard({ toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sGet("/dashboard")
      .then(setData)
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [toast]);

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date()),
    [],
  );

  // Export function - moved inside component but before the conditional return
  const handleExportAll = () => {
    if (!data) return;

    // Create CSV content
    let csvRows = [];

    // Add title and timestamp
    csvRows.push(`"Dashboard Export - ${new Date().toLocaleString()}"`);
    csvRows.push("");

    // Summary section
    csvRows.push('"=== SUMMARY ==="');
    csvRows.push('"Metric","Value"');
    csvRows.push(`"Total Orders","${data.stats?.totalOrders || 0}"`);
    csvRows.push(
      `"Total Revenue","₹${(data.stats?.totalRevenue || 0).toLocaleString()}"`,
    );
    csvRows.push(`"Total Products","${data.stats?.totalProducts || 0}"`);
    csvRows.push(`"Total Customers","${data.stats?.totalUsers || 0}"`);
    csvRows.push("");

    // Recent Orders section
    csvRows.push('"=== RECENT ORDERS ==="');
    csvRows.push('"Order ID","Customer","Amount","Status","Date"');
    if (data.recentOrders && data.recentOrders.length > 0) {
      data.recentOrders.forEach((order) => {
        csvRows.push(
          `"${order.orderId}","${order.user?.name || "Guest"}","₹${order.totalAmount?.toLocaleString()}","${order.orderStatus}","${new Date(order.createdAt).toLocaleDateString("en-IN")}"`,
        );
      });
    }
    csvRows.push("");

    // Order Status section
    csvRows.push('"=== ORDER STATUS BREAKDOWN ==="');
    csvRows.push('"Status","Count"');
    if (data.ordersByStatus && data.ordersByStatus.length > 0) {
      data.ordersByStatus.forEach((status) => {
        csvRows.push(`"${status._id}","${status.count}"`);
      });
    }
    csvRows.push("");

    // Top Products section
    csvRows.push('"=== TOP SELLING PRODUCTS ==="');
    csvRows.push('"Product","Units Sold","Revenue"');
    if (data.topProducts && data.topProducts.length > 0) {
      data.topProducts.forEach((product) => {
        csvRows.push(
          `"${product.name}","${product.sales}","₹${product.revenue?.toLocaleString()}"`,
        );
      });
    }
    csvRows.push("");

    // Low Stock section
    if (data.lowStock && data.lowStock.length > 0) {
      csvRows.push('"=== LOW STOCK ALERTS ==="');
      csvRows.push('"Product","Stock Left"');
      data.lowStock.forEach((item) => {
        csvRows.push(`"${item.name}","${item.stock}"`);
      });
    }

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `dashboard_export_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (toast) toast("Dashboard exported successfully", "success");
  };

  // Early return for loading state - MUST be after all hooks and functions
  if (loading) return <LoadingSpinner />;

  const {
    stats,
    recentOrders = [],
    ordersByStatus = [],
    monthlySales = [],
    topProducts = [],
    lowStock = [],
  } = data || {};

  const totalStatusOrders = ordersByStatus.reduce((s, x) => s + x.count, 0);

  // Main return - ensure all JSX is properly structured
  return (
    <div className="dash">
      {/* Header */}
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Overview</h2>
          <p className="dash-date">{today}</p>
        </div>
        <div className="dash-actions">
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
          >
            <FiRefreshCw /> Refresh
          </Btn>
          <Btn variant="primary" size="sm" onClick={handleExportAll}>
            <FiDownload /> Export
          </Btn>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="dash-kpi-grid">
        <Stat
          icon={<FiBox />}
          label="Total Orders"
          value={stats?.totalOrders?.toLocaleString()}
          color="#6366f1"
          sub="All time volume"
        />
        <Stat
          icon={<FaRupeeSign />}
          label="Total Revenue"
          value={`₹${(stats?.totalRevenue || 0).toLocaleString()}`}
          color="#10b981"
          sub="Excluding cancelled"
        />
        <Stat
          icon={<FiTag />}
          label="Products"
          value={stats?.totalProducts?.toLocaleString()}
          color="#f59e0b"
          sub="Active catalogue"
        />
        <Stat
          icon={<FiUsers />}
          label="Customers"
          value={stats?.totalUsers?.toLocaleString()}
          color="#0ea5e9"
          sub="Registered accounts"
        />
      </div>

      {/* Charts Row */}
      <div className="dash-charts-grid">
        <div className="dash-card">
          <div className="dash-card-header">
            <div
              className="dash-card-icon"
              style={{ background: "rgba(99,102,241,0.1)", color: "#6366f1" }}
            >
              <MdMonetizationOn />
            </div>
            <div>
              <div className="dash-card-title">Monthly Revenue</div>
              <div className="dash-card-sub">Last 12 months</div>
            </div>
          </div>
          <BarChart
            data={monthlySales}
            labelKey="_id"
            valueKey="revenue"
            color="#6366f1"
          />
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <div
              className="dash-card-icon"
              style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}
            >
              <MdShoppingCart />
            </div>
            <div>
              <div className="dash-card-title">Monthly Orders</div>
              <div className="dash-card-sub">Last 12 months</div>
            </div>
          </div>
          <BarChart
            data={monthlySales}
            labelKey="_id"
            valueKey="orders"
            color="#f59e0b"
          />
        </div>
      </div>

      {/* Operations Row */}
      <div className="dash-ops-layout">
        {/* Recent Orders */}
        <div className="dash-card dash-ops-main">
          <div className="dash-card-header dash-card-header--between">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                className="dash-card-icon"
                style={{ background: "rgba(14,165,233,0.1)", color: "#0ea5e9" }}
              >
                <FiClock />
              </div>
              <div>
                <div className="dash-card-title">Recent Orders</div>
                <div className="dash-card-sub">Latest 6 transactions</div>
              </div>
            </div>
            <Btn variant="ghost" size="sm">
              View All <FiArrowRight />
            </Btn>
          </div>
          <Table
            columns={[
              {
                key: "orderId",
                label: "Order ID",
                render: (v) => <span className="dash-order-id">#{v}</span>,
              },
              {
                key: "user",
                label: "Customer",
                render: (v) => (
                  <div className="dash-customer">
                    <div className="dash-customer-avatar">
                      {(v?.name || "G").charAt(0).toUpperCase()}
                    </div>
                    <span>{v?.name || "Guest"}</span>
                  </div>
                ),
              },
              {
                key: "totalAmount",
                label: "Amount",
                render: (v) => (
                  <span className="dash-amount">₹{v?.toLocaleString()}</span>
                ),
              },
              {
                key: "orderStatus",
                label: "Status",
                render: (v) => <Badge status={v} />,
              },
              {
                key: "createdAt",
                label: "Date",
                render: (v) => (
                  <span className="dash-date-cell">
                    {new Date(v).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                ),
              },
            ]}
            data={recentOrders.slice(0, 6)}
            loading={false}
          />
        </div>

        {/* Sidebar panels */}
        <div className="dash-ops-sidebar">
          {/* Fulfillment Status */}
          <div className="dash-card">
            <div className="dash-card-header">
              <div
                className="dash-card-icon"
                style={{ background: "rgba(16,185,129,0.1)", color: "#10b981" }}
              >
                <FiTrendingUp />
              </div>
              <div>
                <div className="dash-card-title">Fulfillment</div>
                <div className="dash-card-sub">Order status breakdown</div>
              </div>
            </div>

            <div className="dash-status-list">
              {ordersByStatus.map((s) => {
                const colors = STATUS_COLORS[s._id] || {};
                const pct =
                  totalStatusOrders > 0
                    ? Math.round((s.count / totalStatusOrders) * 100)
                    : 0;
                return (
                  <div key={s._id} className="dash-status-item">
                    <div className="dash-status-row">
                      <span className="dash-status-name">{s._id}</span>
                      <span className="dash-status-count">
                        {s.count}
                        <span className="dash-status-pct">{pct}%</span>
                      </span>
                    </div>
                    <div className="dash-progress-track">
                      <div
                        className="dash-progress-fill"
                        style={{
                          width: `${pct}%`,
                          background: colors.text || "#6366f1",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Low Stock Alert */}
          {lowStock.length > 0 && (
            <div className="dash-card dash-alert-card">
              <div className="dash-alert-pulse" />
              <div className="dash-card-header">
                <div className="dash-card-icon dash-alert-icon">
                  <FiAlertTriangle />
                </div>
                <div>
                  <div className="dash-card-title dash-alert-title">
                    Low Stock
                  </div>
                  <div className="dash-card-sub">
                    {lowStock.length} products need restocking
                  </div>
                </div>
              </div>

              <div className="dash-alert-list">
                {lowStock.slice(0, 4).map((p) => (
                  <div key={p._id} className="dash-alert-item">
                    <span className="dash-alert-name">{p.name}</span>
                    <span className="dash-alert-qty">{p.stock} left</span>
                  </div>
                ))}
              </div>

              {lowStock.length > 4 && (
                <button className="dash-alert-more">
                  +{lowStock.length - 4} more items
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Top Products */}
      {topProducts.length > 0 && (
        <div className="dash-card">
          <div className="dash-card-header">
            <div
              className="dash-card-icon"
              style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}
            >
              <FaTrophy />
            </div>
            <div>
              <div className="dash-card-title">Top Selling Products</div>
              <div className="dash-card-sub">By revenue & volume</div>
            </div>
          </div>
          <Table
            columns={[
              {
                key: "name",
                label: "Product",
                render: (v) => <span className="dash-product-name">{v}</span>,
              },
              {
                key: "sales",
                label: "Units Sold",
                render: (v) => (
                  <span className="dash-units">
                    <span
                      className="dash-units-bar"
                      style={{ width: `${Math.min(v * 4, 80)}px` }}
                    />
                    {v}
                  </span>
                ),
              },
              {
                key: "revenue",
                label: "Revenue",
                render: (v) => (
                  <span className="dash-revenue">₹{v?.toLocaleString()}</span>
                ),
              },
            ]}
            data={topProducts}
            loading={false}
          />
        </div>
      )}
    </div>
  );
}
